import { useCallback, useEffect, useRef } from "react"
import { Platform, View, ViewStyle } from "react-native"

import { BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import * as NavigationBar from "expo-navigation-bar"
import { Redirect, router, useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { usePostHog } from "posthog-react-native"
import { FeedbackRating } from "types/feedback"
import { TMessage } from "types/transcript"

import { LibraryButton } from "src/components/LibraryButton"
import { SidekickChat } from "src/components/SidekickChat"
import { SidekickView } from "src/components/SidekickView"
import { useAuth } from "src/context/AuthContext"
import { useChannelBrand } from "src/context/ChannelBrandContext"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCurrentMsk, useMskAvailableQuery } from "src/queries/useConfig"
import { useCurrentConversation } from "src/queries/useConversation"
import { useCurrentRole } from "src/queries/useRole"
import { useMemberQuery } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { storage } from "src/utils/storage/mmkv"

function preferredHomeScreenKey(sponsorId: string) {
  return `auth.preferredHomeScreen.${sponsorId}`
}

/**
 * This is the base screen for authenticated users. It sets up the main layout including the safe
 * area view, bottom sheet providers, the main chat view, and the bottom actions bar.
 */
export default function DefaultScreen() {
  const { themed, theme, navTheme } = useAppTheme()
  const { sponsorId } = useAuth()
  // When navigating here after a content item is selected from the CL, skip the CL redirect
  // so the user lands on the chat screen to view the conversation they just started.
  const { skipRedirect } = useLocalSearchParams<{ skipRedirect?: string }>()
  const { pendingChannelId, clearPendingChannelId } = useChannelLink()
  const posthog = usePostHog()
  const channelSwitchTrackedRef = useRef<string | null>(null)
  const memberQuery = useMemberQuery()
  const mskQuery = useMskAvailableQuery()
  const currentMsk = useCurrentMsk()
  const { data: currentConversation } = useCurrentConversation()
  const { brandData, shouldShowSplash } = useChannelBrand()

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  // Handle Android navigation bar - hide gesture handle
  useEffect(() => {
    if (Platform.OS === "android") {
      NavigationBar.setVisibilityAsync("hidden")
      NavigationBar.setBehaviorAsync("overlay-swipe")
      NavigationBar.setBackgroundColorAsync(theme.colors.background)
      NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
    }

    return () => {
      if (Platform.OS === "android") {
        NavigationBar.setVisibilityAsync("visible")
        NavigationBar.setBackgroundColorAsync(theme.colors.background)
        NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
      }
    }
  }, [theme.colors.background, navTheme.dark])

  useEffect(() => {
    if (!pendingChannelId || sponsorId === "unknown-sponsor") return
    if (pendingChannelId === sponsorId) {
      clearPendingChannelId()
      channelSwitchTrackedRef.current = null
      return
    }
    if (channelSwitchTrackedRef.current !== pendingChannelId) {
      channelSwitchTrackedRef.current = pendingChannelId
      posthog?.capture("channel_link_switch_triggered", { channelId: pendingChannelId })
    }
  }, [pendingChannelId, sponsorId, clearPendingChannelId, posthog])

  const onMorePress = useCallback(() => {
    router.push("/menu" as any)
  }, [])

  const onFeedbackPress = useCallback(
    (message?: TMessage, rating?: FeedbackRating) => {
      console.log("in onFeedbackPress with message", message)

      // Determine which message to use for feedback
      const feedbackMessage = message?.text
        ? message
        : currentConversation?.transcript?.slice(-1)[0]

      // Navigate to feedback screen with params
      router.push({
        pathname: "/feedback",
        params: {
          messageId: feedbackMessage?.message_id ?? "",
          messageText: feedbackMessage?.text ?? "",
          rating: rating ?? FeedbackRating.THUMBS_UP,
        },
      })
    },
    [currentConversation],
  )

  // Per-sponsor home-screen preference gating used by the post-splash CL redirect below.
  // (hooks must be called before any conditional returns)
  const currentRole = useCurrentRole()
  const isEndUserRole = currentRole === "member" || currentRole === "testing_member"
  const isMediaMode = brandData?.is_media_mode !== false
  const validSponsor = !!sponsorId && sponsorId !== "unknown-sponsor"
  const homeScreenKey = validSponsor ? preferredHomeScreenKey(sponsorId) : ""
  // undefined = key not set = user has never expressed a preference (first time)
  const preferredHome = homeScreenKey ? storage.getString(homeScreenKey) : "chat"
  const isFirstTime = homeScreenKey !== "" && preferredHome === undefined
  // Redirect to the Content Library when the user prefers it or has never been before.
  // Only applies to end-user roles on media-mode channels once brand data has loaded.
  // Suppressed when the user has just navigated here after selecting a content item,
  // so the chat screen stays visible for the conversation they launched.
  const shouldRedirectToCL =
    !skipRedirect &&
    isMediaMode &&
    validSponsor &&
    brandData !== null &&
    isEndUserRole &&
    (isFirstTime || preferredHome === "content-library")

  // Pressing the library toggle button saves the preference and replaces the current screen.
  const handleGoToLibrary = useCallback(() => {
    if (homeScreenKey) storage.set(homeScreenKey, "content-library")
    router.replace("/content-library")
  }, [homeScreenKey])

  // FIXME: remove this when the backend is updated to always provide this field
  const hasMskInfo = mskQuery.isSuccess && memberQuery.isSuccess
  const hasInvalidMsk = hasMskInfo && !currentMsk
  const newUserValue = memberQuery?.data?.new_user
  const isNewUserUnset = newUserValue === undefined
  const isNewUser = isNewUserUnset || newUserValue
  const needsSelectMsk = hasInvalidMsk || isNewUser
  if (needsSelectMsk) {
    return <Redirect href="/select-msk" />
  }

  if (pendingChannelId && sponsorId !== "unknown-sponsor" && pendingChannelId !== sponsorId) {
    return (
      <Redirect
        href={{
          pathname: "/change-channel",
          params: { autoSwitchChannelId: pendingChannelId },
        }}
      />
    )
  }

  // If the user prefers the Content Library (or hasn't set a preference yet), redirect there.
  // While the channel splash is still showing we wait (the global splash overlay in the
  // layout owns dismissal); once it dismisses, shouldShowSplash flips false and we redirect.
  if (shouldRedirectToCL && !shouldShowSplash) {
    if (isFirstTime && homeScreenKey) storage.set(homeScreenKey, "content-library")
    return (
      <Redirect
        href={{
          pathname: "/content-library",
          params: isFirstTime ? { firstVisit: "1" } : {},
        }}
      />
    )
  }

  return (
    <BottomSheetModalProvider>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />
      <View style={themed($flexOverflow)}>
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          darkBackground={true}
          controlsVisible={true}
          onMenuPress={onMorePress}
          riveOwner="primary"
          disableCCforView={true}
          avatarBackdrop={false}
          showProgressOverlay={false}
          showControlsGradient={false}
          modeToggle={<LibraryButton compact onPress={handleGoToLibrary} />}
        >
          <SidekickChat
            onMorePress={onMorePress}
            onFeedbackPress={onFeedbackPress}
            onBrandPress={handleGoToLibrary}
          />
        </SidekickView>
      </View>
    </BottomSheetModalProvider>
  )
}

const $flexOverflow: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  // On web the minimized/tab avatar escapes the 720 px canvas with negative offsets;
  // overflow: hidden would clip those. Native still gets hidden to contain the chat layout.
  overflow: Platform.OS === "web" ? "visible" : "hidden",
  width: "100%",
  backgroundColor: colors.background,
})
